import { useState, useEffect, useCallback } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import moment from 'moment';
import { createNotification } from '../components/Notifications/Notifications';

export function useLivekitRoom({ serverUrl }) {
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [fullScreen, setFullScreen] = useState('');
  const [raisedHands, setRaisedHands] = useState([]); 
  const [activeDialog, setActiveDialog] = useState('');

  const joinRoom = async (token, mediaParams) => {
    const newRoom = new Room();
    setRoom(newRoom);

    const updateParticipants = () => {
      if (!newRoom) return;
      const remoteParticipants = Array.from(newRoom.remoteParticipants.values());
      const withoutAgent = remoteParticipants.filter((p) => !p.identity.includes('agent'));
      const list = [newRoom.localParticipant, ...withoutAgent];
      setParticipants(list);
    };

    const handleDataReceived = (payload, participant) => {
      const {sid} = participant
      const {username} = participant._attributes
      try {
        const message = JSON.parse(new TextDecoder().decode(payload));

        if (message.type === 'raise_hand') {
          setRaisedHands((prev) => [...prev, sid]);
          createNotification('info', `${username} поднял(а) руку`);
        } else if (message.type === 'end_dialog') {
          setActiveDialog('');
        }
      } catch (e) {
        console.warn('Failed to parse message', e);
      }
    };

    const events = [
      RoomEvent.ActiveSpeakersChanged,
      RoomEvent.TrackMuted,
      RoomEvent.TrackUnmuted,
      RoomEvent.LocalTrackPublished,
      RoomEvent.LocalTrackUnpublished,
      RoomEvent.TrackSubscribed,
      RoomEvent.TrackUnsubscribed,
      RoomEvent.DataReceived,
    ];

    events.forEach((event) => {
      if (event === RoomEvent.DataReceived) {
        newRoom.on(event, handleDataReceived);
      } else {
        newRoom.on(event, updateParticipants);
      }
    });

    await newRoom.connect(serverUrl, token);

    const { videoDeviceId, audioDeviceId } = mediaParams;
    if (videoDeviceId)
      await newRoom.localParticipant.setCameraEnabled(true, { deviceId: videoDeviceId });
    if (audioDeviceId)
      await newRoom.localParticipant.setMicrophoneEnabled(true, { deviceId: audioDeviceId });

    updateParticipants();
    setRaisedHands([]);
    setActiveDialog('');

    return newRoom;
  };


  const startDialogWith = useCallback(
    async (targetId, targetName) => {
      if (!room) return;

      const message = {
        type: 'start_dialog',
        initiatorId: room.localParticipant.identity,
        initiatorName: room.localParticipant._attributes?.username || 'Преподаватель',
        targetId,
        targetName,
      };

      const encoder = new TextEncoder();
      const payload = encoder.encode(JSON.stringify(message));
      await room.localParticipant.publishData(payload, { reliable: true });

      // Открываем диалог у преподавателя
      setActiveDialog(targetId);
    },
    [room]
  );

  const endDialog = useCallback(async () => {
    if (!room) return;

    const message = { type: 'end_dialog' };
    const encoder = new TextEncoder();
    const payload = encoder.encode(JSON.stringify(message));
    await room.localParticipant.publishData(payload, { reliable: true });

    setActiveDialog('');
  }, [room]);

  const clearRaisedHands = useCallback(() => {
    setRaisedHands([]);
  }, []);

  // === Медиа-контролы ===
  const toggleAudio = useCallback(async () => {
    if (!room) return;
    const pub = Array.from(room.localParticipant.audioTrackPublications.values())[0];
    if (pub) await (pub.isMuted ? pub.unmute() : pub.mute());
  }, [room]);

  const toggleVideo = useCallback(async () => {
    if (!room) return;
    const pub = Array.from(room.localParticipant.videoTrackPublications.values()).find(
      (p) => p.source === Track.Source.Camera
    );
    if (pub) await (pub.isMuted ? pub.unmute() : pub.mute());
  }, [room]);

  const toggleScreenShare = useCallback(async () => {
    if (!room) return;
    await room.localParticipant.setScreenShareEnabled(!room.localParticipant.isScreenShareEnabled);
  }, [room]);

  const leaveRoom = useCallback(async () => {
    if (room) {
      await room.disconnect();
      setRoom(null);
      setRaisedHands([]);
      setActiveDialog('');
    }
  }, [room]);

  useEffect(() => {
    return () => {
      if (room) room.disconnect();
    };
  }, [room]);

  return {
    room,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    participants,
    fullScreen,
    setFullScreen,

    raisedHands,
    activeDialog,
    startDialogWith,
    endDialog,
    clearRaisedHands,
  };
}